import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Urls } from "./shared/urls";
import { FilterPreferences } from "./interfaces/filter-preference.interface";
import { ApiService } from "./services/api.service";
import { AssignedTasksFacadeService } from "./services/assigned-tasks-facade.service";
import { firstValueFrom, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { NavigationEnum } from "./enums/navigation.enum";
import { IDelegationContext } from "./interfaces/delegation.interface";
import { redirectIfAllowed } from "./utils/redirect.util";

interface DelegationClaims {
  act?: { sub: string; email?: string };
  has_delegated_access?: boolean;
}

@Injectable()
export class CredentialService {
  private static readonly FILTER_PREFERENCES_KEY = "overviewFilterPreferences";

  user: any;
  token: string;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private readonly assignedTasksFacade: AssignedTasksFacadeService
  ) {
    const u = JSON.parse(localStorage.getItem("user"));
    this.user = u ? u : null;
    const t = localStorage.getItem("token");
    this.token = t ? t : null;
  }

  login(cred): any {
    return this.http.post(Urls.auth, cred).pipe(
      tap((loginResponse: any) => {
        if (loginResponse.user) {
          this.setLogin(loginResponse);
        }
      })
    );
  }

  isAzureSSOEnabled(loginData: { email: string }): Observable<{ redirect_url: string }> {
    return this.http.post<{ redirect_url: string }>(Urls.checkAzureSSO, loginData);
  }

  performAzureLogin(idToken: string, stateToken: string): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(Urls.authenticateAzure, {
      code: idToken,
      state: stateToken
    });
  }

  setLogin(data): void {
    this.token = data["token"];
    this.user = data["user"];
    localStorage.setItem("token", this.token);
    localStorage.setItem("user", JSON.stringify(this.user));
  }

  /**
   * returns true if there is a difference in the user/token (if any) read
   * when the service was initialized and currently in localStorage
   * If true, the application state is inconsistent and a reload is required.
   */
  isUserOrTokenInconsistent() {
    const currentUser: any = this.getUser();
    const currentToken: string = this.getToken();

    if (!this.user || !this.token) {
      if (currentUser && currentToken) {
        return true;
      } else {
        return false;
      }
    }

    if (!currentUser || !currentToken) {
      return true;
    }

    if (currentUser._id !== this.user._id) {
      return true;
    }

    if (currentToken !== this.token) {
      return true;
    }

    return false;
  }

  get delegation(): IDelegationContext {
    const claims = this.tokenClaims();
    return {
      isDelegated: !!claims?.act,
      hasDelegatedAccess: !!claims?.has_delegated_access,
      actorEmail: claims?.act?.email
    };
  }

  /** A selector token only lets an actor choose an environment; it is no AMLCare session. */
  isSelectorSession(): boolean {
    const { hasDelegatedAccess, isDelegated } = this.delegation;
    return hasDelegatedAccess && !isDelegated;
  }

  getPostLoginUrl(): string {
    return this.isSelectorSession() ? `/${NavigationEnum.DelegationSelector}` : "/";
  }

  /** Ends the delegated session and signs the actor in again with Entra, which leads back to the selector. */
  async returnToEnvironmentSelector(): Promise<void> {
    const { redirect_url } = await firstValueFrom(this.http.post<{ redirect_url: string }>(Urls.delegationEnd, {}));
    this.assignedTasksFacade.stopPolling();
    redirectIfAllowed(redirect_url);
  }

  async logoutAndInvalidateToken(): Promise<void> {
    if (this.getToken() && !this.isSelectorSession()) {
      try {
        await this.apiService.put("auth/logout/", { isAppPanel: true });
      } catch (e) {
        console.error(":::CredentialService:::logoutAndInvalidateToken:::error:::", e);
      }
    }
    await this.logout();
  }

  async logout(returnUrl?: string) {
    this.assignedTasksFacade.stopPolling();
    this.user = null;
    this.token = "";
    localStorage.clear();

    let url = "/login";
    if (returnUrl) {
      url += `?returnUrl=${encodeURIComponent(returnUrl)}`;
    }

    window.open(url, "_self");
  }

  getToken() {
    return localStorage.getItem("token");
  }

  private tokenClaims(): DelegationClaims | null {
    try {
      const payload = this.getToken()?.split(".")[1];
      if (!payload) return null;
      return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
      return null;
    }
  }

  getUser() {
    return JSON.parse(localStorage.getItem("user"));
  }

  /** Stored apart from the user, which is replaced by the server's copy (e.g. on a language change) that holds no preferences. */
  private getFilterPreferencesKey(): string | null {
    const userId = this.getUser()?._id;
    return userId ? `${CredentialService.FILTER_PREFERENCES_KEY}:${userId}` : null;
  }

  getFilterPreferences(): FilterPreferences {
    const key = this.getFilterPreferencesKey();
    if (!key) {
      return {};
    }
    try {
      const preferences = JSON.parse(localStorage.getItem(key));
      return preferences && typeof preferences === "object" && !Array.isArray(preferences) ? preferences : {};
    } catch {
      return {};
    }
  }

  setFilterPreferences(preferences: FilterPreferences): void {
    const key = this.getFilterPreferencesKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(preferences));
    } catch (error) {
      console.error(":::CredentialService:::setFilterPreferences():::error:::", error);
    }
  }

  checkForgotEmail(email) {
    return new Promise((resolve, reject) => {
      this.http.post(Urls.forgot, email).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  resetPassword(id, data) {
    return new Promise((resolve, reject) => {
      this.http.post(Urls.baseUrl + `auth/${id}/reset-password`, data).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  // GDPR methods

  generateQRCode() {
    return new Promise((resolve, reject) => {
      this.http.get(Urls.TFA).subscribe(
        TFASetup => {
          resolve(TFASetup);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  verifyTFA(data) {
    return new Promise((resolve, reject) => {
      this.http.put(`${Urls.TFA}user/verify`, data).subscribe(
        TFASetup => {
          resolve(TFASetup);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  updateTFA(data): any {
    return new Promise((resolve, reject) => {
      this.http.put(`${Urls.TFA}user`, data).subscribe(
        TFASetup => {
          resolve(TFASetup);
        },
        err => {
          reject(err);
        }
      );
    });
  }
}
